/**
 * Phone country/dial data + pure helpers, shared by the checkout guest form and
 * the retry-restore path. Kept in lib (not co-located in the client component)
 * so the pure logic is node-testable without dragging React into the test.
 */

export interface PhoneCountry {
  code: string;
  dial: string;
  flag: string;
  name: string;
  placeholder: string;
}

export const PHONE_COUNTRIES: PhoneCountry[] = [
  { code: 'RO', dial: '+40', flag: '\u{1F1F7}\u{1F1F4}', name: 'Romania', placeholder: '7XX XXX XXX' },
  { code: 'DE', dial: '+49', flag: '\u{1F1E9}\u{1F1EA}', name: 'Germany', placeholder: '1XX XXXXXXX' },
  { code: 'AT', dial: '+43', flag: '\u{1F1E6}\u{1F1F9}', name: 'Austria', placeholder: '6XX XXXXXXX' },
  { code: 'HU', dial: '+36', flag: '\u{1F1ED}\u{1F1FA}', name: 'Hungary', placeholder: '20 XXX XXXX' },
  { code: 'BG', dial: '+359', flag: '\u{1F1E7}\u{1F1EC}', name: 'Bulgaria', placeholder: '8X XXX XXXX' },
  { code: 'FR', dial: '+33', flag: '\u{1F1EB}\u{1F1F7}', name: 'France', placeholder: '6 XX XX XX XX' },
  { code: 'IT', dial: '+39', flag: '\u{1F1EE}\u{1F1F9}', name: 'Italy', placeholder: '3XX XXX XXXX' },
  { code: 'ES', dial: '+34', flag: '\u{1F1EA}\u{1F1F8}', name: 'Spain', placeholder: '6XX XXX XXX' },
  { code: 'GB', dial: '+44', flag: '\u{1F1EC}\u{1F1E7}', name: 'United Kingdom', placeholder: '7XXX XXXXXX' },
  { code: 'US', dial: '+1', flag: '\u{1F1FA}\u{1F1F8}', name: 'United States', placeholder: '(XXX) XXX-XXXX' },
  { code: 'NL', dial: '+31', flag: '\u{1F1F3}\u{1F1F1}', name: 'Netherlands', placeholder: '6 XXXX XXXX' },
  { code: 'PL', dial: '+48', flag: '\u{1F1F5}\u{1F1F1}', name: 'Poland', placeholder: 'XXX XXX XXX' },
  { code: 'CZ', dial: '+420', flag: '\u{1F1E8}\u{1F1FF}', name: 'Czech Republic', placeholder: 'XXX XXX XXX' },
  { code: 'CH', dial: '+41', flag: '\u{1F1E8}\u{1F1ED}', name: 'Switzerland', placeholder: '7X XXX XX XX' },
  { code: 'SE', dial: '+46', flag: '\u{1F1F8}\u{1F1EA}', name: 'Sweden', placeholder: '7X XXX XX XX' },

  // Extended set (2026-08-31). Ordered by proximity/relevance to the hotel's
  // markets rather than alphabetically, and appended AFTER the original block so
  // the +1 tie below stays resolved as US (see `parseStoredPhone`).
  { code: 'MD', dial: '+373', flag: '\u{1F1F2}\u{1F1E9}', name: 'Moldova', placeholder: '6X XXX XXX' },
  { code: 'RS', dial: '+381', flag: '\u{1F1F7}\u{1F1F8}', name: 'Serbia', placeholder: '6X XXX XXXX' },
  { code: 'HR', dial: '+385', flag: '\u{1F1ED}\u{1F1F7}', name: 'Croatia', placeholder: '9X XXX XXXX' },
  { code: 'SI', dial: '+386', flag: '\u{1F1F8}\u{1F1EE}', name: 'Slovenia', placeholder: '3X XXX XXX' },
  { code: 'SK', dial: '+421', flag: '\u{1F1F8}\u{1F1F0}', name: 'Slovakia', placeholder: '9XX XXX XXX' },
  { code: 'UA', dial: '+380', flag: '\u{1F1FA}\u{1F1E6}', name: 'Ukraine', placeholder: 'XX XXX XXXX' },
  { code: 'GR', dial: '+30', flag: '\u{1F1EC}\u{1F1F7}', name: 'Greece', placeholder: '69X XXX XXXX' },
  { code: 'TR', dial: '+90', flag: '\u{1F1F9}\u{1F1F7}', name: 'Turkey', placeholder: '5XX XXX XX XX' },
  { code: 'IL', dial: '+972', flag: '\u{1F1EE}\u{1F1F1}', name: 'Israel', placeholder: '5X XXX XXXX' },
  { code: 'BE', dial: '+32', flag: '\u{1F1E7}\u{1F1EA}', name: 'Belgium', placeholder: '4XX XX XX XX' },
  { code: 'IE', dial: '+353', flag: '\u{1F1EE}\u{1F1EA}', name: 'Ireland', placeholder: '8X XXX XXXX' },
  { code: 'PT', dial: '+351', flag: '\u{1F1F5}\u{1F1F9}', name: 'Portugal', placeholder: '9XX XXX XXX' },
  { code: 'DK', dial: '+45', flag: '\u{1F1E9}\u{1F1F0}', name: 'Denmark', placeholder: 'XX XX XX XX' },
  { code: 'NO', dial: '+47', flag: '\u{1F1F3}\u{1F1F4}', name: 'Norway', placeholder: 'XXX XX XXX' },
  { code: 'FI', dial: '+358', flag: '\u{1F1EB}\u{1F1EE}', name: 'Finland', placeholder: '4X XXX XXXX' },
  { code: 'LU', dial: '+352', flag: '\u{1F1F1}\u{1F1FA}', name: 'Luxembourg', placeholder: '6XX XXX XXX' },
  { code: 'CY', dial: '+357', flag: '\u{1F1E8}\u{1F1FE}', name: 'Cyprus', placeholder: '9X XXX XXX' },
  { code: 'AE', dial: '+971', flag: '\u{1F1E6}\u{1F1EA}', name: 'United Arab Emirates', placeholder: '5X XXX XXXX' },
  { code: 'CA', dial: '+1', flag: '\u{1F1E8}\u{1F1E6}', name: 'Canada', placeholder: '(XXX) XXX-XXXX' },
  { code: 'AU', dial: '+61', flag: '\u{1F1E6}\u{1F1FA}', name: 'Australia', placeholder: '4XX XXX XXX' },
];

export function getSelectedPhoneCountry(code: string): PhoneCountry {
  return PHONE_COUNTRIES.find((c) => c.code === code) || PHONE_COUNTRIES[0];
}

/**
 * Split a stored merged phone (`${dial}${local}`, e.g. "+40725942787") back into
 * the dropdown country code + the bare local part — the inverse of how submit
 * composes it (`${selectedPhoneCountry.dial}${formData.phone}`). Used by the
 * retry-restore path to prefill the phone without double-prefixing it (doc 90
 * §2C). Matches the LONGEST dial prefix so "+420…" isn't eaten by "+4…".
 * Returns null when nothing matches (foreign/odd format) so the caller leaves
 * the field blank rather than guessing.
 *
 * AMBIGUOUS DIALS: NANP shares +1 across US and CA, so a stored +1 number cannot
 * be attributed to a country from the digits alone. `Array.prototype.sort` is
 * stable, so among equal-length dials the PHONE_COUNTRIES order decides — US is
 * listed before CA, hence +1 always restores as US. That only affects which flag
 * the dropdown shows on a retry-restore; the phone value itself round-trips
 * byte-identically either way, because submit recomposes `${dial}${local}`.
 */
export function parseStoredPhone(stored: string): { phoneCountry: string; phone: string } | null {
  const trimmed = (stored || '').trim();
  if (!trimmed.startsWith('+')) return null;
  const byLongestDial = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  const match = byLongestDial.find((c) => trimmed.startsWith(c.dial));
  if (!match) return null;
  return { phoneCountry: match.code, phone: trimmed.slice(match.dial.length).trim() };
}
